import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { Info, AlertTriangle } from "lucide-react";

const TensionResult = ({ result }) => {
  // Calculate moving average for tension
  function getMovingAverage(data, windowSize = 3) {
    if (!data || data.length === 0) return [];
    let avgArr = [];
    for (let i = 0; i < data.length; i++) {
      let start = Math.max(0, i - windowSize + 1);
      let window = data.slice(start, i + 1);
      let avg =
        window.reduce((sum, d) => sum + parseFloat(d.tension), 0) /
        window.length;
      avgArr.push({ ...data[i], movingAvg: avg });
    }
    return avgArr;
  }

  // Helper to get the latest moving average value
  function getLatestMovingAvg(data, windowSize = 3) {
    const avgArr = getMovingAverage(data, windowSize);
    return avgArr.length > 0 ? avgArr[avgArr.length - 1].movingAvg : null;
  }

  const [tensionCategory, setTensionCategory] = useState("");
  const [tensionColor, setTensionColor] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [spectrumData, setSpectrumData] = useState([]);
  const canvasRef = useRef(null);
  // const conversionFactor = 1.1926;
  const conversionFactor = 1;

  useEffect(() => {
    if (result) {
      // Categorizar a tensão
      if (result.tension * conversionFactor < 50) {
        setTensionCategory("Low");
        setTensionColor("text-blue-500");
      } else if (
        result.tension * conversionFactor >= 50 &&
        result.tension * conversionFactor < 55
      ) {
        setTensionCategory("Medium");
        setTensionColor("text-green-500");
      } else {
        setTensionCategory("High");
        setTensionColor("text-red-500");
      }

      // Adicionar ao histórico
      setHistoryData((prev) => {
        const newData = [
          ...prev,
          {
            id: Date.now(),
            timestamp: new Date().toLocaleTimeString(),
            frequency: result.frequency.toFixed(2),
            tension: (result.tension * conversionFactor).toFixed(2),
          },
        ];

        // Manter apenas os últimos 10 registros
        if (newData.length > 10) {
          return newData.slice(newData.length - 10);
        }
        return newData;
      });

      // Gerar dados de espectro simulados para visualização
      if (result.frequency) {
        generateSpectrumData(result.frequency);
      }
    }
  }, [result]);

  // Função para gerar dados de espectro simulados
  const generateSpectrumData = (dominantFrequency) => {
    const data = [];
    // Gerar 100 pontos de dados para o espectro
    for (let i = 0; i < 100; i++) {
      const freq = 50 + i * 10; // Frequências de 50Hz a 1050Hz

      // Criar um pico na frequência dominante
      let magnitude = 5 + Math.random() * 10; // Ruído de fundo

      // Adicionar pico na frequência dominante e harmônicos
      if (Math.abs(freq - dominantFrequency) < 30) {
        magnitude = 80 + Math.random() * 20; // Pico principal
      } else if (Math.abs(freq - dominantFrequency * 2) < 20) {
        magnitude = 40 + Math.random() * 15; // Primeiro harmônico
      } else if (Math.abs(freq - dominantFrequency * 3) < 15) {
        magnitude = 20 + Math.random() * 10; // Segundo harmônico
      }

      data.push({
        frequency: freq,
        magnitude: magnitude,
      });
    }
    setSpectrumData(data);
  };

  // Função para renderizar uma visualização de onda no canvas
  useEffect(() => {
    if (result && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const width = canvas.width;
      const height = canvas.height;

      // Limpar o canvas
      ctx.clearRect(0, 0, width, height);

      // Desenhar fundo
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(0, 0, width, height);

      // Desenhar linha central
      ctx.strokeStyle = "#cbd5e1";
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();

      // Desenhar onda senoidal com a frequência detectada
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      ctx.beginPath();

      const frequency = result.frequency;
      const amplitude = height / 3;
      const period = width / (frequency / 50); // Ajustar para visualização

      for (let x = 0; x < width; x++) {
        const y = height / 2 + amplitude * Math.sin((2 * Math.PI * x) / period);
        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.stroke();
    }
  }, [result]);

  if (!result) {
    return (
      <div className="flex flex-col items-center p-6 bg-white rounded-lg shadow-md mt-4">
        <div className="flex items-center justify-center w-full h-40 bg-gray-100 rounded-lg">
          <p className="text-gray-500">
            Record and analyze the string sound to see the results
          </p>
        </div>
      </div>
    );
  }

  // Frequência usada para destaque
  const usedFrequency =
    result.frequencyData && result.frequencyData.usedFrequency
      ? result.frequencyData.usedFrequency
      : result.frequency;

  // Handler para seleção de frequência
  const handleSelectFrequency = (freq) => {
    if (result.onSelectFrequency) {
      result.onSelectFrequency(freq);
    }
  };

  return (
    <div className="flex flex-col p-6 bg-white rounded-lg shadow-md mt-4">
      <h2 className="text-xl font-bold mb-4">Analysis Result</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-500 mb-1">Detected Frequency</span>
          <span className="text-3xl font-bold">
            {result.frequency.toFixed(2)} Hz
          </span>
        </div>

        <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-500 mb-1">Estimated Tension</span>
          <span className={`text-3xl font-bold ${tensionColor}`}>
            {(result.tension * conversionFactor).toFixed(2)} lbs
          </span>
          <span className={`text-sm font-medium mt-1 ${tensionColor}`}>
            {tensionCategory} Tension
          </span>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Spectral Analysis</h3>
        </div>

        <div className="h-64 w-full bg-slate-50 rounded-lg p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={
                result.frequencyData && result.frequencyData.spectrum
                  ? result.frequencyData.frequencies.map((freq, idx) => ({
                      frequency: freq,
                      magnitude: result.frequencyData.spectrum[idx],
                      isUsed: freq === usedFrequency,
                    }))
                  : spectrumData
              }
              margin={{ top: 5, right: 30, left: 20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="frequency"
                label={{
                  value: "Frequency (Hz)",
                  position: "insideBottomRight",
                  offset: -15,
                }}
              />
              <YAxis
                label={{
                  value: "Magnitude",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip
                formatter={(value) => [`${value}`, "Magnitude"]}
                labelFormatter={(label) => `${label} Hz`}
              />
              <Bar dataKey="magnitude" isAnimationActive={false}>
                {result.frequencyData &&
                  result.frequencyData.spectrum &&
                  result.frequencyData.frequencies.map((freq, idx) => (
                    <Cell
                      key={idx}
                      fill={freq === usedFrequency ? "#ff4136" : "#8884d8"}
                    />
                  ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {result.frequencyData && result.frequencyData.validPeaks && (
          <div className="flex flex-wrap gap-2 mt-4">
            {result.frequencyData.validPeaks.map((peak, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectFrequency(peak.frequency)}
                className={`text-sm py-1 px-2 rounded-lg ${
                  peak.frequency === usedFrequency
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {peak.frequency} Hz
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center mb-2">
          <h3 className="text-lg font-semibold">Interpretation</h3>
          <Info size={16} className="ml-2 text-gray-500" />
        </div>

        <div className="p-4 bg-blue-50 rounded-lg">
          {tensionCategory === "Low" && (
            <p>
              Low tension (below 50 lbs) generally provides more power and less
              control. Ideal for players seeking more power in their shots.
            </p>
          )}

          {tensionCategory === "Medium" && (
            <p>
              Medium tension (between 50-55 lbs) offers a good balance between
              power and control. Suitable for most recreational and intermediate
              players.
            </p>
          )}

          {tensionCategory === "High" && (
            <p>
              High tension (above 55 lbs) provides more control and less power.
              Preferred by advanced players who generate their own power and
              prioritize precision.
            </p>
          )}
        </div>
      </div>

      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded mb-4">
        <div className="flex">
          <AlertTriangle
            size={20}
            className="text-yellow-600 mr-2 flex-shrink-0"
          />
          <p className="text-sm text-yellow-800">
            This is an estimate based on frequency analysis. Accuracy may vary
            depending on the environment, microphone quality, and string type.
            For more accurate results, take multiple measurements and use the
            average.
          </p>
        </div>
      </div>

      {historyData.length > 1 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Measurement History</h3>
          {/* Display current moving average value */}
          <div className="mb-2 text-gray-700">
            Moving average (last 3):{" "}
            <span className="font-bold">
              {getLatestMovingAvg(historyData, 3)?.toFixed(2) ?? "--"} lbs
            </span>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={getMovingAverage(historyData, 3)}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" />
                <YAxis domain={["auto", "auto"]} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="tension"
                  name="Tension (lbs)"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="movingAvg"
                  name="Moving Avg (lbs)"
                  stroke="#f59e42"
                  strokeDasharray="5 5"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default TensionResult;
